import { useCallback, useState } from 'react'
import { Link } from 'wouter'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  isErrorReturn,
  Provider,
  isSuccessReturn
} from '@guestgoodie/auth-core'

import {
  Mail01Icon,
  GoogleIcon,
  SquareLock01Icon
} from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import Input from '@/components/ui/Input'
import { useAuth } from '../hooks/useAuth'
import { useErrorToText } from '../hooks/useErrorCode'
import VerificationCodeForm from './VerificationCodeForm'
import { ForgotPassword } from './ForgotPassword'

type FormData = {
  email: string
  password: string
  rememberMe: boolean
}

export default function SignIn() {
  const { t } = useLingui()
  const { client, refresh } = useAuth()

  const getError = useErrorToText()
  const [verification, setVerification] = useState<FormData | undefined>()
  const [serverError, setServerError] = useState<Form.ServerError>()

  const submit = useCallback(
    async (form: FormData) => {
      const { email, password, rememberMe } = form

      const response = await client.signIn({
        email,
        password,
        remember_me: rememberMe
      })

      if (isErrorReturn(response)) {
        if (response.errorCode === 'email_verification_needed') {
          setVerification(form)

          return
        }

        setServerError({
          message: getError(response.errorCode),
          type: response.errorCode
        })

        return
      }

      if (isSuccessReturn(response)) {
        refresh()

        return
      }
    },
    [client, getError, refresh, setServerError, setVerification]
  )

  if (verification) {
    return <VerificationCodeForm {...verification} />
  }

  return (
    <>
      <Form.Root<FormData>
        onSubmit={submit}
        serverErrors={
          serverError
            ? {
                root: { serverError }
              }
            : undefined
        }
        defaultValues={{
          email: '',
          password: '',
          rememberMe: false
        }}
      >
        <div className="flex flex-col gap-4 px-2.5">
          <h1 className="text-text text-heading1 text-center">
            <Trans>Sign-In</Trans>
          </h1>
          <div className="flex flex-col gap-6">
            <Form.Field
              name="email"
              label={t`Email`}
              rules={{
                validate: {
                  emailValidation: Form.Validations.emailValidation(
                    t`Not a valid email.`
                  ),
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="email"
                  Icon={Mail01Icon}
                  onChange={event => onChange(event.target.value)}
                  placeholder=""
                  autoComplete="email webauthn work"
                  {...props}
                />
              )}
            </Form.Field>
            <Form.Field
              name="password"
              label={t`Password`}
              rules={{
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="password"
                  Icon={SquareLock01Icon}
                  onChange={event => onChange(event.target.value)}
                  placeholder=""
                  autoComplete="current-password webauthn"
                  {...props}
                />
              )}
            </Form.Field>
            <div className="flex flex-row justify-between">
              <Form.Field name="rememberMe">
                {({ renderProps: { onChange, value, ...props } }) => (
                  <Checkbox
                    checked={value}
                    onChange={() => onChange(!value)}
                    {...props}
                  >
                    <Trans>Stay logged in</Trans>
                  </Checkbox>
                )}
              </Form.Field>
              <ForgotPassword />
            </div>
          </div>
          <div className="text-center">
            <Form.Error name="root.serverError" />
          </div>
          <div className="flex flex-col justify-evenly gap-4 p-2 md:flex-row">
            <Link to={`/sign-up${location.search ?? ''}`} asChild>
              <Button variant="outline" type="button" className="flex-1">
                <Trans>Create Account</Trans>
              </Button>
            </Link>
            <Form.Submit className="flex-1">
              <Trans>Sign-in</Trans>
            </Form.Submit>
          </div>
        </div>
      </Form.Root>
      <div className="w-full bg-text-light h-[2px] rounded my-4" />
      <div className="flex flex-col gap-1.5 p-2">
        <Button
          Icon={GoogleIcon}
          className="bg-secondary border-secondary hover:border-secondary-dark hover:bg-secondary-dark focus:outline-primary"
          onClick={() => client.signInWithProvider(Provider.GOOGLE)}
        >
          <Trans>Sign-in with Google</Trans>
        </Button>
      </div>
    </>
  )
}
